import React, { useEffect, useRef } from 'react';

interface SpectrumVisualizerProps {
  analyserNode: AnalyserNode | null;
  width?: number;
  height?: number;
  barColor?: string;
  barGap?: number;
}

const SpectrumVisualizer: React.FC<SpectrumVisualizerProps> = ({
  analyserNode,
  width = 300,
  height = 150,
  barColor = "#FACC15", // Tailwind yellow-500
  barGap = 2,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameIdRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!analyserNode || !canvas) {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
      // Clear canvas if analyserNode becomes null or canvas is not available
      if (canvas && canvas.getContext('2d')) {
        const ctx = canvas.getContext('2d');
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
      }
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error("Failed to get canvas 2D context.");
      return;
    }

    // Set canvas dimensions
    // For simplicity, directly setting width/height. Proper DPI scaling might be needed for sharper visuals.
    canvas.width = width;
    canvas.height = height;
    
    // Use a fixed number of bins for visualization, or a portion of available bins
    // to avoid overly dense visuals if frequencyBinCount is very high.
    // analyserNode.fftSize must be a power of 2, frequencyBinCount is fftSize / 2.
    const relevantBinCount = analyserNode.frequencyBinCount; // Use all bins initially, can be adjusted
    const dataArray = new Uint8Array(relevantBinCount);

    const draw = () => {
      // Re-check analyserNode in each frame in case it's disconnected or becomes null
      if (!analyserNode) {
        if (animationFrameIdRef.current) {
          cancelAnimationFrame(animationFrameIdRef.current);
          animationFrameIdRef.current = null;
        }
        ctx.clearRect(0, 0, canvas.width, canvas.height); // Clear canvas on stop
        return;
      }

      analyserNode.getByteFrequencyData(dataArray);
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Consider using a subset of bins for a cleaner look, e.g., lower frequencies.
      // For example, use only the first 1/2 or 1/4 of the bins.
      const numBarsToDisplay = Math.min(relevantBinCount, 256); // Cap at 256 bars for example
      
      // If relevantBinCount is very high, barWidth can become < 0.
      // We need to ensure numBarsToDisplay makes sense with width and barGap.
      let numBars = numBarsToDisplay;
      let barWidth = (canvas.width - (numBars - 1) * barGap) / numBars;

      // Adjust numBars if barWidth is too small or negative
      if (barWidth <= 0) {
        numBars = Math.floor(canvas.width / (1 + barGap)); // Min bar width of 1px
        barWidth = 1; 
      }
      
      let x = 0;

      for (let i = 0; i < numBars; i++) {
        // If numBars was adjusted, we might need to map `i` to an index in `dataArray`
        // or simply use the first `numBars` from `dataArray`.
        // For simplicity, if numBars is less than numBarsToDisplay, we use the first numBars.
        const dataArrayIndex = i; // Could be more sophisticated if skipping bins from a larger dataArray
        
        const barHeightFraction = dataArray[dataArrayIndex] / 255; // Normalize to 0-1 range
        const barHeight = barHeightFraction * canvas.height;
        
        ctx.fillStyle = barColor;
        ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
        
        x += barWidth + barGap;
      }
      animationFrameIdRef.current = requestAnimationFrame(draw);
    };

    // Start the drawing loop
    animationFrameIdRef.current = requestAnimationFrame(draw);

    // Cleanup function to cancel animation when component unmounts or dependencies change
    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
    };
  }, [analyserNode, width, height, barColor, barGap]);

  // If analyserNode is not available, don't render the canvas.
  // A placeholder could be rendered here if desired.
  if (!analyserNode) {
    // Example placeholder:
    // return <div style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #ccc', color: '#ccc' }}>Visualizer unavailable</div>;
    return null; 
  }

  // Use width and height attributes directly on the canvas element for its drawing surface size.
  // CSS can still control the display size if needed, but this is often preferred for canvas.
  return <canvas ref={canvasRef} width={width} height={height} />;
};

export default SpectrumVisualizer;
